import { useEffect, useState } from 'react'
import { Table, Dropdown, Avatar } from 'antd'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import cx from 'classnames'
import moment from 'moment'

import Styles from './ManageMembers.module.scss'
import { useOnScroll } from 'hooks/UseOnScroll'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { SettingsMember, RootState, SettingsGuest, MembershipInvitation, DeletedMembership } from 'types'
import { ReactComponent as Checkmark } from 'assets/icons/check16.svg'
import { ReactComponent as Ellipses } from 'assets/icons/more16.svg'
import { getInitials } from 'utils'
import { reportError } from 'utils/error'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { ManageGuests } from './ManageGuests'
import { IconButton } from 'components/IconButton/IconButton'
import { RemoveGuestContextProvider } from './RemoveGuestContext'
import { ErrorResponse } from 'request-dot-js'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { RemoveUserConfirmationModal } from './RemoveUserConfirmationModal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Heading } from 'blaze-ui/Heading/Heading'

const getFullName = (user: SettingsMember | SettingsGuest) => {
  if (user.last_name) return `${user.first_name} ${user.last_name}`

  return user.first_name ? user.first_name : user.email
}

const getUserRole = (user: SettingsMember | SettingsGuest) => {
  if (user.type === 'User') {
    return user.role
  }

  if (user.type === 'DeletedMembership') return 'Inactive'

  if (user.type === 'Invitation') {
    return `Invited${user.role === 'Admin' ? ' Admin' : ''}`
  }

  return 'Guest'
}

const keyAndNameAndRoleFields = (user: SettingsMember | SettingsGuest) => {
  const fullName = getFullName(user)
  return {
    key: user.id,
    name: (
      <div className={Styles.nameCell}>
        <Avatar className={Styles.userAvatar} src={user.avatar_url || undefined}>
          {!user.avatar_url ? getInitials(fullName) : ''}
        </Avatar>
        <p title={fullName} className={Styles.nameCellText}>
          {fullName}
        </p>
      </div>
    ),
    role: getUserRole(user),
  }
}

const lastActivity = (user: SettingsMember | SettingsGuest) => {
  if (!user.last_activity_at) return 'Not Available'
  return moment(user.last_activity_at).format('ll')
}

const inviteSent = (user: MembershipInvitation) => {
  if (!user.invited_at) return 'Not Available'
  return moment(user.invited_at).format('ll')
}

export const currentAndGuestUserData = (user: SettingsMember | SettingsGuest) => {
  return {
    ...keyAndNameAndRoleFields(user),
    email: (
      <p title={user.email} className={Styles.emailCellText}>
        {user.email}
      </p>
    ),
    dateAdded: moment(user.confirmed_at || user.created_at).format('ll'),
    lastActive: lastActivity(user),
  }
}

const invitedUserData = (user: MembershipInvitation) => {
  return {
    ...keyAndNameAndRoleFields(user),
    invitedBy: user.invitation_sender?.display_name || 'Not Available',
    inviteSent: inviteSent(user),
  }
}

const removedAt = (user: DeletedMembership) => {
  if (!user.deleted_at) return 'Not Available'
  return moment(user.deleted_at).format('ll')
}

const formerUserData = (user: DeletedMembership) => {
  return {
    ...keyAndNameAndRoleFields(user),
    // removedBy: 'test', // We aren't currently saving this data
    removedAt: removedAt(user),
  }
}

const NAME_AND_ROLE_COLUMNS = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: 'Role',
    dataIndex: 'role',
    key: 'role',
  },
]

export const CURRENT_AND_GUEST_COLUMNS = [
  ...NAME_AND_ROLE_COLUMNS,
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
  },
  {
    title: 'Date added',
    dataIndex: 'dateAdded',
    key: 'dateAdded',
  },
  {
    title: 'Last active',
    dataIndex: 'lastActive',
    key: 'lastActive',
  },
  {
    title: '',
    dataIndex: 'actionMenu',
    key: 'actionMenu',
  },
]

export const MOBILE_CURRENT_AND_GUEST_COLUMNS = [
  ...NAME_AND_ROLE_COLUMNS,
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
  },
  {
    title: '',
    dataIndex: 'actionMenu',
    key: 'actionMenu',
  },
]

export const MOBILE_MD_CURRENT_AND_GUEST_COLUMNS = [
  ...NAME_AND_ROLE_COLUMNS,
  {
    title: '',
    dataIndex: 'actionMenu',
    key: 'actionMenu',
  },
]

const INVITED_COLUMNS = [
  ...NAME_AND_ROLE_COLUMNS,
  {
    title: 'Invited by',
    dataIndex: 'invitedBy',
    key: 'invitedBy',
  },
  {
    title: 'Invite sent',
    dataIndex: 'inviteSent',
    key: 'inviteSent',
  },
  {
    title: '',
    dataIndex: 'actionMenu',
    key: 'actionMenu',
  },
]

const MOBILE_INVITED_COLUMNS = [
  ...NAME_AND_ROLE_COLUMNS,
  {
    title: 'Invited by',
    dataIndex: 'invitedBy',
    key: 'invitedBy',
  },
  {
    title: '',
    dataIndex: 'actionMenu',
    key: 'actionMenu',
  },
]

const MOBILE_MD_INVITED_COLUMNS = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: 'Invited by',
    dataIndex: 'invitedBy',
    key: 'invitedBy',
  },
  {
    title: '',
    dataIndex: 'actionMenu',
    key: 'actionMenu',
  },
]

const FORMER_COLUMNS = [
  ...NAME_AND_ROLE_COLUMNS,
  // "Removed by" isn't available at this time
  // {
  //   title: 'Removed by',
  //   dataIndex: 'removedBy',
  //   key: 'removedBy',
  // },
  {
    title: 'Date removed',
    dataIndex: 'removedAt',
    key: 'removedAt',
  },
  {
    title: '',
    dataIndex: 'actionMenu',
    key: 'actionMenu',
  },
]

interface Props extends RouteComponentProps {
  setUsers: (users: SettingsMember[]) => void
  reloadUsers: () => void
  users: SettingsMember[]
  guests: SettingsGuest[]
  auth: RootState['auth']
}

export const ManageMembers = ({ auth, users, setUsers, reloadUsers, guests }: Props) => {
  const [invitedUsers, setInvitedUsers] = useState<SettingsMember[]>([])
  const [activeUsers, setActiveUsers] = useState<SettingsMember[]>([])
  const [inactiveUsers, setInactiveUsers] = useState<SettingsMember[]>([])
  const [selectedUser, setSelectedUser] = useState<SettingsMember | SettingsGuest | null>(null)
  const [isMember, setIsMember] = useState<Boolean>(false)
  const [isAdmin, setIsAdmin] = useState<Boolean>(false)
  const [isSuperAdmin, setIsSuperAdmin] = useState<Boolean>(false)
  const [confirmRemovalVisible, setConfirmRemovalVisible] = useState<boolean>(false)
  const { isMobile, isMobileMd } = useResponsiveDesign()

  const columns =
    (isMobileMd && MOBILE_MD_CURRENT_AND_GUEST_COLUMNS) ||
    (isMobile && MOBILE_CURRENT_AND_GUEST_COLUMNS) ||
    CURRENT_AND_GUEST_COLUMNS

  const invitedColumns =
    (isMobileMd && MOBILE_MD_INVITED_COLUMNS) || (isMobile && MOBILE_INVITED_COLUMNS) || INVITED_COLUMNS

  useEffect(() => {
    setInvitedUsers(users.filter((user) => user.type === 'Invitation'))
    setInactiveUsers(users.filter((user) => user.type === 'DeletedMembership'))
    setActiveUsers(users.filter((user) => user.type === 'User'))
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const currentUserRole = users.find((user) => user.id === auth!.id)?.role
    setIsMember(currentUserRole === 'User')
    setIsAdmin(currentUserRole === 'Admin')
    setIsSuperAdmin(currentUserRole === 'SuperAdmin')
  }, [users, auth])

  useOnScroll(() => {
    if (selectedUser === null) return

    setSelectedUser(null)
  })

  const updateRole = async (id: number, type: SettingsMember['type'], role: SettingsMember['role']) => {
    if (type === 'DeletedMembership' || !auth) {
      return
    }
    setSelectedUser(null)
    const res = await client.updateMemberRole(id, role, auth.company_key)
    if (res.type === 'success') {
      showToastMessage('Role was updated', 'success')
      setUsers(res.data.data.members)
    } else {
      const error = res as ErrorResponse
      const errorMsg =
        error.status === 403
          ? "You don't have permissions to perform this change."
          : 'There was a problem updating that role. Please try again'
      showErrorMessageToast(errorMsg)
      if (res.type === 'error' && res.status !== 403)
        reportError(new Error('ManageMembers received an error response'), {
          severity: 'warning',
          metaData: { info: 'Update role' },
        })
    }
  }

  const actionsMenu = (user: SettingsMember) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (user.id === auth!.id || isMember || (isAdmin && user.role === 'SuperAdmin')) return

    const dropdownContent = (
      <div className={Styles.actionsDropdownContent}>
        {user.type === 'Invitation' && (
          <div className={Styles.action} onClick={() => resendInvitation(user.id)}>
            Resend Invitation
          </div>
        )}
        {user.type === 'User' && (
          <>
            <div
              className={cx(Styles.action, { [Styles.active]: user.role === 'User' })}
              onClick={() => {
                updateRole(user.id, user.type, 'User')
              }}
            >
              User
              {user.role === 'User' && <Checkmark />}
            </div>

            <div
              className={cx(Styles.action, { [Styles.active]: user.role === 'Admin' })}
              onClick={() => {
                updateRole(user.id, user.type, 'Admin')
              }}
            >
              Admin
              {user.role === 'Admin' && <Checkmark />}
            </div>

            {isSuperAdmin && (
              <div
                className={cx(Styles.action, { [Styles.active]: user.role === 'SuperAdmin' })}
                onClick={() => {
                  updateRole(user.id, user.type, 'SuperAdmin')
                }}
              >
                Super Admin
                {user.role === 'SuperAdmin' && <Checkmark />}
              </div>
            )}
          </>
        )}

        {user.type !== 'DeletedMembership' && (
          <>
            <div className={Styles.divider} />
            <div className={Styles.action} onClick={() => setConfirmRemovalVisible(true)}>
              {user.type === 'Invitation' ? 'Cancel Invite' : 'Remove From Workspace'}
            </div>
          </>
        )}
        {user.type === 'DeletedMembership' && (
          <>
            <div className={Styles.action} onClick={() => grantAccess(user.id, false)}>
              Grant Member Access
            </div>
            <div className={Styles.action} onClick={() => grantAccess(user.id, true)}>
              Grant Admin Access
            </div>
          </>
        )}
      </div>
    )
    return (
      <Dropdown
        placement="bottomRight"
        overlay={dropdownContent}
        trigger={['click']}
        onVisibleChange={(open: boolean) => setSelectedUser(open ? user : null)}
      >
        <IconButton className={Styles.moreActions} aria-label="manage user" data-testid="manageUser" icon={Ellipses} />
      </Dropdown>
    )
  }

  const resendInvitation = async (id: number) => {
    setSelectedUser(null)
    const res = await client.resendInvitation(id, auth ? auth.company_key : '')
    if (res.type === SUCCESS_RESPONSE_TYPE) {
      showToastMessage('Invitation sent! 🎉', 'success')
    } else {
      showErrorMessageToast('There was a problem sending your invitation. Please try again')
    }
  }

  const grantAccess = async (id: number, admin: boolean) => {
    setSelectedUser(null)
    const res = await client.reactivateMember(id, admin, auth ? auth.company_key : '')
    if (res.type === SUCCESS_RESPONSE_TYPE) {
      showToastMessage('Member reactivated! 🎉', 'success')
      reloadUsers()
    } else {
      showErrorMessageToast('There was a problem trying to grant access to that user. Please try again')
    }
  }
  return (
    <>
      <RemoveUserConfirmationModal
        visible={confirmRemovalVisible && !!selectedUser}
        setVisible={setConfirmRemovalVisible}
        onSuccess={reloadUsers}
        onDone={() => setSelectedUser(null)}
        memberToRemove={selectedUser}
      />
      <div className={Styles.container}>
        <div className={Styles.userSection}>
          <Table
            className={Styles.membersTable}
            rowClassName={() => Styles.membersTableRow}
            dataSource={activeUsers.map((user) => ({
              ...currentAndGuestUserData(user),
              actionMenu: actionsMenu(user),
            }))}
            columns={columns}
            pagination={false}
          />
        </div>
        {!!guests.length && (
          <RemoveGuestContextProvider auth={auth}>
            <ManageGuests
              guests={guests}
              auth={auth}
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              reloadUsers={reloadUsers}
            />
          </RemoveGuestContextProvider>
        )}
        {!!invitedUsers.length && (
          <div className={Styles.userSection}>
            <div className={Styles.newSectionTitle}>
              <Heading level={5}>Invited Users</Heading>
              <span className={Styles.sectionTitleCount}>({invitedUsers.length})</span>
            </div>
            <Table
              className={Styles.membersTable}
              rowClassName={() => Styles.membersTableRow}
              dataSource={invitedUsers.map((user) => ({
                ...invitedUserData(user as MembershipInvitation),
                actionMenu: actionsMenu(user),
              }))}
              columns={invitedColumns}
              pagination={false}
            />
          </div>
        )}
        {!!inactiveUsers.length && (
          <div className={Styles.userSection}>
            <div className={Styles.newSectionTitle}>
              <Heading level={5}>Former</Heading>
              <span className={Styles.sectionTitleCount}>({inactiveUsers.length || 0})</span>
            </div>
            <Table
              className={Styles.membersTable}
              rowClassName={() => Styles.membersTableRow}
              dataSource={inactiveUsers.map((user) => ({
                ...formerUserData(user as DeletedMembership),
                actionMenu: actionsMenu(user),
              }))}
              columns={FORMER_COLUMNS}
              pagination={false}
            />
          </div>
        )}
      </div>
    </>
  )
}

export default withRouter(ManageMembers)
